import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'
import { isMobile } from 'react-device-detect'

import { BottomMenuItem, Props as BottomMenuItemProps } from './BottomMenuItem'
import { MenuItem, Props as MenuItemProps } from './MenuItem'

export interface Props {
  address?: string
  bottomMenuItems?: BottomMenuItemProps[]
  languageCode: string
  languagePrefix: string | null
  locality?: string
  menuItems: MenuItemProps[]
  navigationStatus?: boolean
  phone?: string
}

export const Navigation = memo(function Navigation({
  address,
  bottomMenuItems,
  locality,
  menuItems,
  navigationStatus,
  phone,
}: Props) {
  return (
    <Container className={navigationStatus ? 'visible' : undefined}>
      {menuItems ? (
        <Nav>
          <NavList>
            {menuItems.slice(0, menuItems.length / 2).map((item, index) => (
              <NavLi key={index}>
                <MenuItem
                  index={index}
                  navigationStatus={navigationStatus}
                  {...item}
                />
              </NavLi>
            ))}
          </NavList>
          <NavList>
            {menuItems
              .slice(menuItems.length / 2, menuItems.length)
              .map((item, index) => (
                <NavLi key={index}>
                  <MenuItem
                    index={index}
                    navigationStatus={navigationStatus}
                    {...item}
                  />
                </NavLi>
              ))}
          </NavList>
        </Nav>
      ) : null}

      <Wrapper
        className={navigationStatus ? 'visible' : undefined}
        dial={4}
        row
        space="between"
        wrap
      >
        {bottomMenuItems ? (
          <BottomMenu>
            {bottomMenuItems.map((item, index) => (
              <BottomMenuItem key={index} {...item} />
            ))}
          </BottomMenu>
        ) : null}

        <Contacts row wrap>
          {address ? (
            <Address>{`${address}${locality ? `-${locality}` : null}`}</Address>
          ) : null}
          {phone ? (
            <Phone
              href={`tel:${phone}`}
              onClick={() => {
                if (isMobile) {
                  typeof window.gtag !== 'undefined' &&
                    window.gtag('event', 'Click', {
                      event_category: 'Website',
                      event_label: 'Mobile Phone',
                    })
                }
              }}
            >
              {phone}
            </Phone>
          ) : null}
        </Contacts>
      </Wrapper>
    </Container>
  )
})

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralDark1};
  opacity: 0;
  overflow-y: auto;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  transition: 0.6s ease-in-out;
  -webkit-overflow-scrolling: auto;
  &.visible {
    opacity: 1;
  }
  &:before {
    content: '';
    width: 70%;
    height: 80%;
    background: url('/wave.svg') no-repeat;
    background-size: cover;
    opacity: 0.06;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 1199px) {
    display: block;
    padding-bottom: 3.75rem;
  }
`

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 14vh 5vw 10rem 10vw;
  position: relative;
  z-index: 2;

  @media (max-width: 1199px) {
    padding: 5.625rem 10vw 0;
    position: relative;
    top: auto;
    right: auto;
    left: auto;
  }
`

const NavList = styled.ul`
  width: calc(50% - 4vw);

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const NavLi = styled.li`
  margin-top: 4.5vh;

  @media (max-width: 1199px) {
    margin-top: 4vh;
    > div {
      opacity: 1 !important;
      animation: none !important;
    }
  }
`

const Wrapper = styled(FlexBox)`
  opacity: 0;
  position: fixed;
  bottom: -6vh;
  right: 10vw;
  left: 10vw;
  transition: 0.6s 1s;
  z-index: 2;
  &.visible {
    opacity: 1;
    bottom: 6vh;
  }
  &:after {
    content: '';
    width: 80vw;
    height: 13vh;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    position: absolute;
    top: -3vh;
    left: 0;
  }

  @media (max-width: 1199px) {
    display: block;
    margin-top: 4.5vh;
    padding: 0 10vw;
    position: relative;
    bottom: auto;
    right: auto;
    left: auto;
    transition: none;
    &.visible {
      bottom: auto;
    }
    &:after {
      display: none;
    }
  }
`

const BottomMenu = styled.div`
  position: relative;
  z-index: 2;

  > div {
    display: flex;
    flex-wrap: wrap;
  }
`

const Contacts = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.8125rem;
  letter-spacing: 0.0163rem;
  line-height: 1.25rem;
  position: relative;
  z-index: 2;

  @media (max-width: 1199px) {
    margin-top: 6.7vh;
  }
`

const Address = styled.div`
  margin-right: 1.875rem;

  @media (max-width: 767px) {
    width: 100%;
    margin-right: 0;
  }
`

const Phone = styled.a`
  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 767px) {
    margin-top: 0.75rem;
  }
`
